import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import es from "./locales/es.json";
import en from "./locales/en.json";
import pt from "./locales/pt.json";
import fr from "./locales/fr.json";
import APP_LOCALES from "./appLocales";

// iter64 corte 5 — lightweight i18n (no external deps).
// iter72 corte 2 — PT y FR completos + detección geo (CF-IPCountry vía /api/geo/detect).
//
// RC-WEB-5 GATE 14 — ÚNICO contrato de resolución de idioma, idéntico para web,
// Android e iOS:
//   1. idioma explícito guardado en la CUENTA (`users.language`)
//   2. elección manual en este dispositivo (localStorage `avisocar_lang`)
//   3. idioma del navegador/dispositivo
//   4. default regional configurado (país -> idioma)
//   5. fallback determinista `es`
// El país NUNCA fija el idioma de forma permanente: en cuanto la persona elige,
// se guarda en la cuenta y viaja con ella a cualquier cliente.

const LOCALES = {
  es: { ...es, app: APP_LOCALES.es },
  en: { ...en, app: APP_LOCALES.en },
  pt: { ...pt, app: APP_LOCALES.pt },
  fr: { ...fr, app: APP_LOCALES.fr },
};
const SUPPORTED = ["es", "en", "pt", "fr"];

export const LANGS = [
  { code: "es", label: "Español", short: "ES" },
  { code: "en", label: "English", short: "EN" },
  { code: "pt", label: "Português", short: "PT" },
  { code: "fr", label: "Français", short: "FR" },
];

// LatAm+España→ES · Brasil+Portugal→PT · Francia→FR · India/USA/Canadá/resto→EN
const ES_COUNTRIES = ["ES", "MX", "GT", "HN", "SV", "NI", "CR", "PA", "CO", "VE", "EC",
  "PE", "BO", "CL", "AR", "UY", "PY", "CU", "DO", "PR"];

export function countryToLang(iso) {
  const c = (iso || "").toUpperCase();
  if (!c) return null;
  if (ES_COUNTRIES.includes(c)) return "es";
  if (c === "BR" || c === "PT") return "pt";
  if (c === "FR") return "fr";
  return "en";
}

export function hasManualLang() {
  try { return SUPPORTED.includes(localStorage.getItem("avisocar_lang")); } catch (_e) { return false; }
}

export function getInitialLang() {
  try {
    const stored = localStorage.getItem("avisocar_lang");
    if (stored && SUPPORTED.includes(stored)) return stored;
    // CORTE 4-B 2.2 — idioma de la cuenta gana sobre la detección geo
    const acct = localStorage.getItem("avisocar_account_lang");
    if (acct && SUPPORTED.includes(acct)) return acct;
    const geo = localStorage.getItem("avisocar_geo_lang");
    if (geo && SUPPORTED.includes(geo)) return geo;
    const country = localStorage.getItem("avisocar_pricing_country");
    if (country) return countryToLang(country) || "es";
    const nav = (navigator.language || "es").toLowerCase().slice(0, 2);
    if (SUPPORTED.includes(nav)) return nav;
  } catch (_e) { /* ignore */ }
  return "es";
}

function lookup(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

const I18nContext = createContext({ lang: "es", cmsLang: "es", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  // Detección geo en la primera visita — solo si el usuario nunca eligió manualmente.
  useEffect(() => {
    if (hasManualLang()) return;
    let cached = null;
    try { cached = localStorage.getItem("avisocar_geo_lang"); } catch (_e) { /* ignore */ }
    if (cached) return; // ya detectado antes
    fetch("/api/geo/detect")
      .then((r) => r.json())
      .then((d) => {
        const detected = SUPPORTED.includes(d?.lang) ? d.lang : null;
        if (!detected) return;
        try { localStorage.setItem("avisocar_geo_lang", detected); } catch (_e) { /* ignore */ }
        if (!hasManualLang()) setLangState(detected);
      })
      .catch(() => { /* offline → heurística local ya aplicada */ });
  }, []);

  const setLangSoft = useCallback((l) => {
    if (!hasManualLang() && SUPPORTED.includes(l)) setLangState(l);
  }, []);

  // CORTE 4-B 2.2 — resolución: manual > país de la cuenta > detección.
  const setLangAccount = useCallback((l) => {
    if (!SUPPORTED.includes(l)) return;
    try { localStorage.setItem("avisocar_account_lang", l); } catch (_e) { /* ignore */ }
    if (!hasManualLang()) setLangState(l);
  }, []);

  // RC-WEB-5 GATE 14 — prioridad 1: el idioma EXPLÍCITO de la cuenta. Manda
  // sobre la elección local del dispositivo y sobre el país. Si la cuenta no
  // tiene idioma explícito se conserva el comportamiento anterior (país).
  const setAccountLanguage = useCallback((explicit, countryCode) => {
    if (explicit && SUPPORTED.includes(explicit)) {
      try {
        localStorage.setItem("avisocar_lang", explicit);
        localStorage.setItem("avisocar_account_lang", explicit);
      } catch (_e) { /* ignore */ }
      setLangState(explicit);
      return;
    }
    const derived = countryToLang(countryCode);
    if (derived) setLangAccount(derived);
  }, [setLangAccount]);

  // `persist`: una elección explícita del cliente autenticado se guarda en la
  // cuenta para que viaje a la web, Android e iOS.
  const setLang = useCallback((l, { persist = true } = {}) => {
    if (!SUPPORTED.includes(l)) return;
    setLangState(l);
    try {
      localStorage.setItem("avisocar_lang", l);
      localStorage.setItem("avisocar_account_lang", l);
    } catch (_e) { /* ignore */ }
    if (persist && axios.defaults.headers.common["Authorization"]) {
      axios.put("/api/user/locale", { language: l }).catch(() => { /* best-effort */ });
    }
  }, []);

  const t = useCallback((key, vars) => {
    const dict = LOCALES[lang] || LOCALES.es;
    let val = lookup(dict, key);
    if (val === undefined) val = lookup(LOCALES.es, key);
    if (val === undefined) return key;
    if (typeof val === "string" && vars) {
      Object.entries(vars).forEach(([k, v]) => {
        val = val.split(`{${k}}`).join(String(v));
      });
    }
    return val;
  }, [lang]);

  // CMS: ES/EN/PT/FR servidos por /api/site-content?lang=X (fallback ES por clave).
  const cmsLang = lang;

  return (
    <I18nContext.Provider value={{ lang, cmsLang, setLang, setLangSoft, setLangAccount, setAccountLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
